import cookie from 'cookie';
import { api } from 'services/auth';
import store from 'state/buildStore';
import { parseJwt } from 'utils/parseJWT';

import { LoginModel } from './model/login.model';
import { RegisterModel } from './model/register.model';

export type LoginResult =
    | 'success'
    | 'failed'
    | 'expired_code'
    | { status: 'incorrect_code'; session: string }
    | '';

/**
 * Service to manage AWS Cognito authentication.
 */
export class AuthService {
    get userEmail(): string | null {
        return localStorage.getItem('user_email');
    }

    set userEmail(value: string | null) {
        if (!value) {
            localStorage.removeItem('user_email');
        } else {
            localStorage.setItem('user_email', value);
        }
    }

    /**
     * Indicates if the user is already logged in.
     */
    async isLoggedIn(): Promise<boolean> {
        const cookies = cookie.parse(document.cookie);
        const accessToken = cookies.accessToken;
        const refreshToken = cookies.refreshToken;

        if (!accessToken) {
            return false;
        }

        const tokenData = parseJwt(accessToken);
        const currentTime = Math.floor(Date.now() / 1000);

        if (tokenData && tokenData.exp > currentTime) {
            return true;
        }

        if (!refreshToken) {
            return false;
        }

        const newTokens = await this.refreshAndStoreTokens(refreshToken);
        if (!newTokens) {
            return false;
        }

        return true;
    }

    /**
     * Indicates if the user is already logged in.
     */
    async refreshAndStoreTokens(refreshToken: string): Promise<any> {
        try {
            const newTokens = await store.dispatch(
                api.endpoints.refreshTokens.initiate({
                    refreshToken,
                }),
            );

            if ('error' in newTokens) {
                return false;
            }

            if (newTokens.data) {
                this.setAuthCookies(
                    newTokens.data.idToken,
                    newTokens.data.accessToken,
                    newTokens.data.refreshToken,
                );
                return newTokens.data;
            }
        } catch (e) {
            return null;
        }
    }

    /**
     * Clean up stored data.
     */
    cleanUp(): void {
        // For disclaimer (top banner) purposes
        // https://selina.atlassian.net/browse/PDP-109
        sessionStorage.removeItem('show_disclaimer');
    }

    /**
     * Executes OTP request.
     */
    async requestOTP(email: string): Promise<string | 'failed'> {
        const result = await store.dispatch(
            api.endpoints.requestOTP.initiate({
                email,
            }),
        );

        if ('error' in result) {
            return 'failed';
        }

        return result.data.session;
    }

    /**
     * Executes OTP confirm.
     */
    async confirmOTP(credentials: LoginModel): Promise<LoginResult> {
        this.userEmail = credentials.email;
        const result = await store.dispatch(
            api.endpoints.confirmOTP.initiate({
                email: credentials.email,
                otp: credentials.otp,
                session: credentials.session,
            }),
        );

        this.cleanUp();

        if ('error' in result) {
            if ('status' in result.error && result.error.status === 440) {
                return 'expired_code';
            }
            return 'failed';
        }

        if (Number(result.data.parameter?.attemptsLeft) > 0) {
            return { status: 'incorrect_code', session: result.data.session };
        }

        this.setAuthCookies(
            result.data.idToken,
            result.data.accessToken,
            result.data.refreshToken,
        );

        return 'success';
    }

    setAuthCookies = (
        idToken: string,
        accessToken: string,
        refreshToken: string,
    ) => {
        const cookies = [
            { name: 'idToken', value: idToken },
            {
                name: 'accessToken',
                value: accessToken,
            },
            {
                name: 'refreshToken',
                value: refreshToken,
            },
        ];

        cookies.forEach(({ name, value }) => {
            document.cookie = cookie.serialize(name, value, {
                path: '/',
                secure: true,
                httpOnly: false,
                sameSite: 'none',
                domain: window._env_.SELINA_DOMAIN,
            });
        });
    };

    clearAuthCookies = () => {
        ['idToken', 'accessToken', 'refreshToken'].forEach((name) => {
            document.cookie = cookie.serialize(name, '', {
                path: '/',
                secure: true,
                httpOnly: false,
                sameSite: 'none',
                domain: window._env_.SELINA_DOMAIN,
                expires: new Date(0),
            });
        });
    };

    /**
     * Executes logout request.
     */
    async logout(): Promise<void> {
        try {
            this.userEmail = null;
            const cookies = cookie.parse(document.cookie);
            const { accessToken, refreshToken } = cookies;

            if (accessToken && refreshToken) {
                await store.dispatch(
                    api.endpoints.logout.initiate({
                        accessToken,
                        refreshToken,
                    }),
                );
            }

            this.clearAuthCookies();
            this.cleanUp();
            localStorage.clear();
        } catch (error) {
            console.log('Error signing out: ', error);
        }
    }

    /**
     * Sign up a new user.
     */
    async register(value: RegisterModel): Promise<boolean | 'user_exists'> {
        this.userEmail = value.email;
        const result = await store.dispatch(
            api.endpoints.register.initiate({
                username: value.email,
            }),
        );

        if ('error' in result) {
            const status = (result.error as any)?.status;

            if (status === 500) {
                return 'user_exists';
            }

            return false;
        }

        return true;
    }
}
